import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Save } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { TextInput } from "../../../ui/components/Form";
import { useAuth } from "../../lib/auth/hooks";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import * as productsApi from "../../lib/api/products";
import type { Component, DataCatalog } from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import * as s from "./ProductPage.css";

const COMPONENT_TYPES = [
  "ems", "wcm", "ems_c",
  "pcs", "inv", "inverter",
  "pdu", "battery",
  "dcdc", "pmu", "hvac",
  "ugm", "axion", "mppt", "gen",
] as const;

interface FormValues {
  componentType: string;
  name: string;
  componentIndex: string;
  iotComponentId: string;
  isActive: boolean;
  serial: string;
  macAddress: string;
  manufacturer: string;
  model: string;
  hwVersion: string;
  fwVersion: string;
  dataCatalogId: string;
}

function componentToForm(c: Component): FormValues {
  return {
    componentType: c.componentType,
    name: c.name ?? "",
    componentIndex: c.componentIndex != null ? String(c.componentIndex) : "",
    iotComponentId: c.iotComponentId ?? "",
    isActive: c.isActive,
    serial: c.serial ?? "",
    macAddress: c.macAddress ?? "",
    manufacturer: c.manufacturer ?? "",
    model: c.model ?? "",
    hwVersion: c.hwVersion ?? "",
    fwVersion: c.fwVersion ?? "",
    dataCatalogId: c.dataCatalogId ?? "",
  };
}

function sectionDirty(keys: (keyof FormValues)[], values: FormValues, saved: FormValues) {
  return keys.some((k) => String(values[k] ?? "").trim() !== String(saved[k] ?? "").trim());
}

const DETAILS_KEYS: (keyof FormValues)[] = ["componentType", "name", "componentIndex", "iotComponentId", "isActive"];
const IDENTIFIERS_KEYS: (keyof FormValues)[] = ["serial", "macAddress", "manufacturer", "model", "hwVersion", "fwVersion"];
const TELEMETRY_KEYS: (keyof FormValues)[] = ["dataCatalogId"];

export function ComponentEditPage({
  componentId,
}: { componentId: string }) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();

  const [component, setComponent] = React.useState<Component | null>(null);
  const [dataCatalogs, setDataCatalogs] = React.useState<DataCatalog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [values, setValues] = React.useState<FormValues>({} as FormValues);
  const [savedValues, setSavedValues] = React.useState<FormValues>({} as FormValues);
  const [saving, setSaving] = React.useState<string | null>(null);
  const [sectionError, setSectionError] = React.useState<Record<string, string | null>>({});
  const [dangerExpanded, setDangerExpanded] = React.useState(false);
  const [sectionMessage, setSectionMessage] = React.useState<Record<string, string | null>>({});

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      productsApi.getComponentById(token, componentId),
      productsApi.fetchDataCatalogs(token),
    ])
      .then(([comp, catalogs]) => {
        setComponent(comp);
        setDataCatalogs(catalogs.filter((c) => c.isActive));
        const form = componentToForm(comp);
        setValues(form);
        setSavedValues(form);
      })
      .catch((err) => setLoadError(err?.message || "Failed to load component"))
      .finally(() => setLoading(false));
  }, [token, componentId]);

  const patch = (section: string, field: keyof FormValues, value: string | boolean) => {
    setSectionError((prev) => ({ ...prev, [section]: null }));
    setSectionMessage((prev) => ({ ...prev, [section]: null }));
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSection = async (
    section: string,
    buildPayload: () => productsApi.UpdateComponentPayload,
  ) => {
    if (!token || !component) return;
    setSaving(section);
    setSectionError((prev) => ({ ...prev, [section]: null }));
    setSectionMessage((prev) => ({ ...prev, [section]: null }));
    try {
      const updated = await productsApi.updateComponent(token, component.productId, componentId, buildPayload());
      setComponent(updated);
      const form = componentToForm(updated);
      setValues(form);
      setSavedValues(form);
      setSectionMessage((prev) => ({ ...prev, [section]: "Changes saved." }));
    } catch (err: any) {
      setSectionError((prev) => ({ ...prev, [section]: err?.message || "Failed to save." }));
    } finally {
      setSaving(null);
    }
  };

  const renderSectionActions = (section: string, dirty: boolean, onSave: () => void) => (
    <div className={clsx(s.cardActions, !dirty && s.cardActionsEnd)}>
      {dirty ? <span className={s.changedBadge}>{tx("pro.products.unsavedChanges")}</span> : null}
      <Button
        type="button"
        color="primary"
        variant="solid"
        iconLeft={<Save size={16} />}
        loading={saving === section}
        disabled={!dirty}
        onClick={onSave}
      >
        Save changes
      </Button>
    </div>
  );

  const handleBack = () =>
    navigate({ to: "/workspace/components/$componentId", params: { componentId } });

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>{tx("shared.common.loading")}</Text>
        </div>
      </div>
    );
  }

  if (loadError || !component) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button className={s.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> Component
          </button>
          <Callout variant="error">{loadError ?? "Component not found."}</Callout>
        </div>
      </div>
    );
  }

  const detailsDirty = sectionDirty(DETAILS_KEYS, values, savedValues);
  const identifiersDirty = sectionDirty(IDENTIFIERS_KEYS, values, savedValues);
  const telemetryDirty = sectionDirty(TELEMETRY_KEYS, values, savedValues);
  const defaultTypeCatalog = React.useMemo(
    () => dataCatalogs.find((c) => c.slug === `component-${values.componentType}`) ?? null,
    [dataCatalogs, values.componentType],
  );

  return (
    <div className={s.page}>
      <div className={s.pageColumn}>
        <button className={s.backLink} onClick={handleBack}>
          <ArrowLeft size={14} /> {component.name ?? component.componentType.toUpperCase()}
        </button>

        <Text level="h1" weight="bold" style={{ marginBottom: 16 }}>
          Edit component
        </Text>

        <div className={s.sections}>

          {/* Details */}
          <Card maxWidth={960} className={clsx(s.sectionCard, detailsDirty && s.sectionCardChanged)}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.components.details")}</Text>
              </div>
              {sectionError.details && <Callout variant="error">{sectionError.details}</Callout>}
              {sectionMessage.details && <Callout variant="success">{sectionMessage.details}</Callout>}

              <div className={s.fieldGroup}>
                <span className={s.fieldLabel}>{tx("pro.components.componentType")}</span>
                <select
                  value={values.componentType}
                  onChange={(e) => patch("details", "componentType", e.target.value)}
                  disabled={saving === "details"}
                  className={s.catalogSelect}
                >
                  {COMPONENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type.toUpperCase()}</option>
                  ))}
                </select>
              </div>

              <div className={s.fieldRow}>
                <TextInput
                  label={t("pro.components.displayName")}
                  value={values.name}
                  onChange={(e) => patch("details", "name", e.target.value)}
                  disabled={saving === "details"}
                  changed={values.name.trim() !== savedValues.name.trim()}
                  placeholder={t("pro.components.placeholder.displayName")}
                />
                <div style={{ width: 120, flexShrink: 0 }}>
                  <TextInput
                    label={t("pro.components.index")}
                    value={values.componentIndex}
                    onChange={(e) => patch("details", "componentIndex", e.target.value)}
                    disabled={saving === "details"}
                    changed={values.componentIndex !== savedValues.componentIndex}
                    placeholder="0"
                  />
                </div>
              </div>

              <TextInput
                label={t("pro.components.iotComponentId")}
                value={values.iotComponentId}
                onChange={(e) => patch("details", "iotComponentId", e.target.value)}
                disabled={saving === "details"}
                changed={values.iotComponentId.trim() !== savedValues.iotComponentId.trim()}
                placeholder={t("pro.components.placeholder.iotComponentId")}
              />

              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={values.isActive}
                  onChange={(e) => patch("details", "isActive", e.target.checked)}
                  disabled={saving === "details"}
                />
                Active (include in telemetry polling)
              </label>

              {renderSectionActions("details", detailsDirty, () =>
                handleSaveSection("details", () => ({
                  componentType: values.componentType,
                  name: values.name.trim() || undefined,
                  componentIndex: values.componentIndex ? parseInt(values.componentIndex, 10) : undefined,
                  iotComponentId: values.iotComponentId.trim() || undefined,
                  isActive: values.isActive,
                }))
              )}
            </div>
          </Card>

          {/* Identifiers */}
          <Card maxWidth={960} className={clsx(s.sectionCard, identifiersDirty && s.sectionCardChanged)}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.products.identifiers")}</Text>
              </div>
              {sectionError.identifiers && <Callout variant="error">{sectionError.identifiers}</Callout>}
              {sectionMessage.identifiers && <Callout variant="success">{sectionMessage.identifiers}</Callout>}

              <div className={s.fieldRow}>
                <TextInput
                  label={t("pro.products.serialNumber")}
                  value={values.serial}
                  onChange={(e) => patch("identifiers", "serial", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.serial.trim() !== savedValues.serial.trim()}
                />
                <TextInput
                  label={t("pro.products.macAddress")}
                  value={values.macAddress}
                  onChange={(e) => patch("identifiers", "macAddress", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.macAddress.trim() !== savedValues.macAddress.trim()}
                  placeholder="AA:BB:CC:DD:EE:FF"
                />
              </div>
              <div className={s.fieldRow}>
                <TextInput
                  label={t("pro.components.manufacturer")}
                  value={values.manufacturer}
                  onChange={(e) => patch("identifiers", "manufacturer", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.manufacturer.trim() !== savedValues.manufacturer.trim()}
                />
                <TextInput
                  label={t("pro.products.model")}
                  value={values.model}
                  onChange={(e) => patch("identifiers", "model", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.model.trim() !== savedValues.model.trim()}
                />
              </div>
              <div className={s.fieldRow}>
                <TextInput
                  label={t("pro.components.hardwareVersion")}
                  value={values.hwVersion}
                  onChange={(e) => patch("identifiers", "hwVersion", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.hwVersion.trim() !== savedValues.hwVersion.trim()}
                  placeholder="e.g. 1.0"
                />
                <TextInput
                  label={t("pro.components.firmwareVersion")}
                  value={values.fwVersion}
                  onChange={(e) => patch("identifiers", "fwVersion", e.target.value)}
                  disabled={saving === "identifiers"}
                  changed={values.fwVersion.trim() !== savedValues.fwVersion.trim()}
                  placeholder="e.g. 2.1.4"
                />
              </div>

              {renderSectionActions("identifiers", identifiersDirty, () =>
                handleSaveSection("identifiers", () => ({
                  serial: values.serial.trim() || undefined,
                  macAddress: values.macAddress.trim() || undefined,
                  manufacturer: values.manufacturer.trim() || undefined,
                  model: values.model.trim() || undefined,
                  hwVersion: values.hwVersion.trim() || undefined,
                  fwVersion: values.fwVersion.trim() || undefined,
                }))
              )}
            </div>
          </Card>

          {/* Telemetry */}
          <Card maxWidth={960} className={clsx(s.sectionCard, telemetryDirty && s.sectionCardChanged)}>
            <div className={s.cardInner}>
              <div className={s.sectionHeader}>
                <Text level="h3" weight="bold">{tx("pro.products.telemetry")}</Text>
              </div>
              {sectionError.telemetry && <Callout variant="error">{sectionError.telemetry}</Callout>}
              {sectionMessage.telemetry && <Callout variant="success">{sectionMessage.telemetry}</Callout>}

              <div className={s.fieldGroup}>
                <span className={s.catalogSelectLabel}>{tx("pro.components.dataCatalog")}</span>
                <select
                  value={values.dataCatalogId}
                  onChange={(e) => patch("telemetry", "dataCatalogId", e.target.value)}
                  disabled={saving === "telemetry"}
                  className={s.catalogSelect}
                >
                  <option value="">— Use default for type —</option>
                  {dataCatalogs.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <span style={{ fontSize: 11, marginTop: 2 }} className={s.componentDetailText}>
                  {values.dataCatalogId
                    ? "This catalog overrides the component-type default."
                    : defaultTypeCatalog
                      ? `Using default: ${defaultTypeCatalog.name}`
                      : `No default catalog configured for component type "${values.componentType}".`}
                </span>
              </div>

              {renderSectionActions("telemetry", telemetryDirty, () =>
                handleSaveSection("telemetry", () => ({
                  dataCatalogId: values.dataCatalogId || undefined,
                }))
              )}
            </div>
          </Card>

          {/* Danger Zone */}
          <Card maxWidth={960} className={s.sectionCard} style={{ borderColor: "#FCA5A5" }}>
            <div className={s.cardInner}>
              <button
                type="button"
                onClick={() => setDangerExpanded(e => !e)}
                aria-expanded={dangerExpanded}
                style={{ all: "unset", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", cursor: "pointer", boxSizing: "border-box" }}
              >
                <Text level="h3" weight="bold" style={{ color: "#DC2626" }}>{tx("pro.products.dangerZone")}</Text>
                <svg style={{ flexShrink: 0, color: "#DC2626", transition: "transform 200ms ease", transform: dangerExpanded ? "rotate(180deg)" : "rotate(0deg)" }} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              {dangerExpanded && <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginTop: 16 }}>
                <div>
                  <Text level="body-medium" weight="medium">{tx("pro.components.removeComponent")}</Text>
                  <Text level="body-small" style={{ color: "#6B7280", marginTop: 2 }}>
                    Permanently remove this component from the product. This cannot be undone.
                  </Text>
                </div>
                <Button
                  type="button"
                  color="danger"
                  variant="outline"
                  onClick={async () => {
                    if (!token || !component) return;
                    const ok = await confirm(
                      "Remove component?",
                      `Permanently remove "${component.name ?? component.componentType}" from this product? This cannot be undone.`,
                      "Remove",
                    );
                    if (!ok) return;
                    try {
                      await productsApi.deleteComponent(token, component.productId, componentId);
                      navigate({ to: "/workspace/products/$productId", params: { productId: component.productId } });
                    } catch (err: any) {
                      setSectionError((prev) => ({ ...prev, danger: err?.message || "Failed to remove component." }));
                    }
                  }}
                >
                  Remove
                </Button>
              </div>}
              {dangerExpanded && sectionError.danger && <Callout variant="error">{sectionError.danger}</Callout>}
            </div>
          </Card>

        </div>
      </div>
      <ConfirmModal />
    </div>
  );
}
